export const COMPARISON_MODES = ["day", "week", "month", "quarter", "year"] as const;
export type ComparisonMode = typeof COMPARISON_MODES[number];

export const COMPARISON_OPTIONS = ["previous", "same_last", "last_n", "custom"] as const;
export type ComparisonOption = typeof COMPARISON_OPTIONS[number];

export const DAY_SUB_RANGES = ["week", "month", "year"] as const;
export type DaySubRange = typeof DAY_SUB_RANGES[number];

export interface ComparisonUrlState {
  cmp_mode: ComparisonMode | null;
  cmp_option: ComparisonOption | null;
  cmp_count: number | null;
  cmp_sub: DaySubRange | null;
  cmp_from: string | null;
  cmp_to: string | null;
  cmp_dim: string | null;
}

export interface ComparisonState extends ComparisonUrlState {
  isActive: boolean;
  activate: (params: {
    mode: ComparisonMode;
    option: ComparisonOption;
    count?: number;
    subRange?: DaySubRange;
    customFrom?: string;
    customTo?: string;
  }) => void;
  setDimension: (dim: string | null) => void;
  exit: () => void;
}

// API response types
export interface ComparisonSeriesItem {
  category: string;
  bar_value: number;
  line_value: number;
  bar_id: string | null;
  line_id: string | null;
  offset: string | null;
}

export interface ComparisonWidgetConfig {
  bar: {
    name: string;
    data_point: string;
    selector: string;
    value_formatter: string;
  };
  line: {
    name: string;
    data_point: string;
    selector: string;
    value_formatter: string;
  };
  mark_lines: any[];
  sort_by: string;
}

export interface ComparisonWidgetData {
  config: ComparisonWidgetConfig;
  data: {
    series: ComparisonSeriesItem[];
  };
}

export interface ComparisonWidget {
  type: string;
  title: string;
  widget_data: ComparisonWidgetData;
}

export interface DimensionSeriesDataItem {
  id: string;
  category: string;
  value: number;
}

export interface DimensionSeriesEntry {
  name: string;
  series_data: DimensionSeriesDataItem[];
}

export interface DimensionMetricConfig {
  selector: string;
  direction: string;
  value_formatter: { prefix: string; postfix: string };
  name: string;
}

export interface DimensionMetric {
  config: DimensionMetricConfig;
  data: {
    series: DimensionSeriesEntry[];
  };
}

export interface DimensionWidget {
  type: string;
  title: string;
  widget_data: DimensionMetric[];
}

export interface ComparisonApiResponse {
  result_meta: {
    result_id: string | null;
    result_type: string;
  };
  widgets: (ComparisonWidget | DimensionWidget)[];
  entity_id?: string;
  comparison_mode?: ComparisonMode;
}

// Options config for the ComparisonSelect dropdown
export interface OptionConfig {
  key: ComparisonOption;
  label: string;
  hasSubRange?: boolean;
  subRanges?: readonly string[];
  hasCounter?: boolean;
  min?: number;
  max?: number;
  suffix?: string;
  hasDatePicker?: boolean;
  datePickerType?: "day" | "month" | "quarter" | "year";
}

export const OPTIONS_BY_MODE: Record<ComparisonMode, OptionConfig[]> = {
  day: [
    { key: "previous", label: "Previous day" },
    { key: "same_last", label: "Same day last", hasSubRange: true, subRanges: DAY_SUB_RANGES },
    { key: "last_n", label: "Last", hasCounter: true, min: 1, max: 7, suffix: "days" },
    { key: "custom", label: "Custom date", hasDatePicker: true, datePickerType: "day" },
  ],
  week: [
    { key: "previous", label: "Previous week" },
    { key: "same_last", label: "Same week last month" },
    { key: "last_n", label: "Last", hasCounter: true, min: 1, max: 5, suffix: "weeks" },
  ],
  month: [
    { key: "previous", label: "Previous month" },
    { key: "same_last", label: "Same month last year" },
    { key: "last_n", label: "Last", hasCounter: true, min: 1, max: 3, suffix: "months" },
    { key: "custom", label: "Custom month", hasDatePicker: true, datePickerType: "month" },
  ],
  quarter: [
    { key: "previous", label: "Previous quarter" },
    { key: "same_last", label: "Same quarter last year" },
    { key: "last_n", label: "Last", hasCounter: true, min: 1, max: 3, suffix: "quarters" },
    { key: "custom", label: "Custom quarter", hasDatePicker: true, datePickerType: "quarter" },
  ],
  year: [
    { key: "previous", label: "Previous year" },
    { key: "last_n", label: "Last", hasCounter: true, min: 1, max: 3, suffix: "years" },
    { key: "custom", label: "Custom year", hasDatePicker: true, datePickerType: "year" },
  ],
};
